import React, {
  useEffect, useMemo, useRef, useState, useCallback, Suspense, startTransition
} from "react";
import {
  Box, Container, Grid, Typography, InputBase, useTheme, useMediaQuery,
  IconButton, Paper, List, ListItemButton, ListItemText,
  Stack, Skeleton, Collapse, Badge, Tooltip,
  Button, Chip, Link as MuiLink, CardActionArea, Divider
} from "@mui/material";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { isCareBusinessEligible } from "../../../core/business/category";

// Icons â€” keep these in the import block (NOT later)
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import EventIcon from "@mui/icons-material/Event";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import { useApolloClient, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import jwtDecode from "jwt-decode";

import officePlaceHolderImage from "../../assets/images/business-image-placeholder.png";
import { useVertical } from "../../verticals/useVertical";
import { HOME_ADAPTERS } from "../../verticals/registry";

import {
  BUSINESS_STATES,
  BUSINESS_CITIES,
  BUSINESSES_PAGED,
  GET_TOKEN,
  SEARCH as SEARCH_BUSINESSES,
} from "../../graphql/queries";
import { UPDATE_DEFAULT_BUSINESS } from "../../graphql/mutations";
import { CLAIM_AND_MAP } from "../../graphql/mutations/care";
import { GET_JOB_POSTINGS_BY_BUSINESS } from "../../graphql/queries/careJobs";
import useDebouncedValue from "../../careComponents/hooks/useDebouncedValue";
import formatCityStateZip from "../../careComponents/utils/formatCityStateZip";
import { getWithTTL, setWithTTL } from "../../util/cache/simpleCache";
import { getToken, setToken } from "../../util/authUtils";
import {
  BOOLEAN_AMENITY_KEYS,
  CITIES_TTL,
  COUNTRY_ID,
  DASHBOARD_URL,
  DEFAULT_RADIUS,
  INITIAL_LIMIT,
  PAGE_LIMIT,
  PENDING_KEY,
  STATES_TTL,
  buildAmenitiesInput,
  buildHomeSearchSuggestions,
  createDefaultAdvancedFeatures,
  decodeAmenities,
  encodeAmenities,
  formatMiles,
  idOf,
  isClaimed,
  isZip,
  mergeDedupeAndSort,
  normalizeLicense,
  shallowEq,
  sortByMatchThenClaimed,
} from "./homeShared";
import AdvancedSearchInline from "./home/AdvancedSearchInline";
import HomeDialogs from "./home/HomeDialogs";

// ðŸ”» AFTER all import declarations, you can do dynamic imports / lazy:
const ResponsiveBusinessCard = React.lazy(() => import("./responsiveBusinessCard"));
const AvailableBedsBadge = React.lazy(() => import("../../careComponents/components/availableBedsBadge"));
const AGREEMENT_CONTEXT_KEY = "pendingAgreementBusinessContext";
const DEFAULT_BROWSE_LIMIT = 8;
const FEATURED_CLAIMED_LIMIT = 100;

const compactDate = new Intl.DateTimeFormat("en-US", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const cleanText = (value) => String(value || "").trim();

const firstPhoneNumber = (business) => {
  const phone = business?.phoneNumbers?.find((row) => cleanText(row?.phoneNumber))?.phoneNumber;
  return cleanText(phone) || "Not listed";
};

const businessCategoryLabel = (business) => {
  return (
    cleanText(business?.businessSubcategory?.name) ||
    cleanText(business?.businessSubcategory?.parentCategory?.name) ||
    cleanText(business?.businessTopCategory?.name) ||
    "Care provider"
  );
};

const isCareDirectoryBusiness = (business) => {
  return (
    isCareBusinessEligible(business?.businessSubcategory, business) ||
    isCareBusinessEligible(business?.businessTopCategory, business)
  );
};

const businessLicense = (business) => {
  return cleanText(business?.careSettings?.afhLicenseNumber) || "Not listed";
};

const businessBeds = (business) => {
  const total = Number(business?.careMeta?.bedsTotal ?? 0);
  const open = Number(business?.careMeta?.bedsOpen ?? 0);
  const openingsText =
    open > 0 ? ` (${open} ${open === 1 ? "opening" : "openings"})` : "";
  if (total > 0) return `${total} Licensed${openingsText}`;
  if (open > 0) return `${open} Open`;
  return "Not listed";
};

const businessSpecialty = (business) => {
  return cleanText(business?.careSettings?.specialty) || "Not listed";
};

const publicCareStatusLabel = (business) => {
  const licenseStatus = cleanText(business?.careSettings?.licenseStatus).toUpperCase();
  if (licenseStatus) {
    if (["ACTIVE", "OPEN", "OP", "EXISTING"].includes(licenseStatus)) return "Active";
    if (["INACTIVE", "CLOSED", "CL", "REVOKED", "SUSPENDED", "TERMINATED", "EXPIRED"].includes(licenseStatus)) {
      return "Inactive";
    }
    return licenseStatus
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  return cleanText(business?.status).toUpperCase() === "INACTIVE" ? "Inactive" : "Active";
};

const businessAddedLabel = (business) => {
  if (!business?.createdAt) return "";
  const date = new Date(business.createdAt);
  if (Number.isNaN(date.getTime())) return "";
  return `Added ${compactDate.format(date)}`;
};

const CareRecordFact = ({ icon: Icon, label, value, tone = "blue", onAction, actionLabel }) => {
  const tones = {
    blue: { bg: "#eef4ff", color: "#2563eb" },
    purple: { bg: "#f5efff", color: "#7c3aed" },
    green: { bg: "#eafaf1", color: "#16a34a" },
    amber: { bg: "#fff7e6", color: "#f59e0b" },
    rose: { bg: "#fff0f3", color: "#e11d48" },
  };
  const color = tones[tone] || tones.blue;

  const handleAction = (event) => {
    if (!onAction) return;
    event.preventDefault();
    event.stopPropagation();
    onAction();
  };

  const handleKeyDown = (event) => {
    if (!onAction || (event.key !== "Enter" && event.key !== " ")) return;
    handleAction(event);
  };

  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="flex-start"
      role={onAction ? "link" : undefined}
      tabIndex={onAction ? 0 : undefined}
      aria-label={actionLabel}
      onClick={handleAction}
      onKeyDown={handleKeyDown}
      sx={{
        minWidth: 0,
        cursor: onAction ? "pointer" : "default",
        borderRadius: 2,
        outline: "none",
        "&:focus-visible": onAction
          ? {
              boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.22)",
            }
          : undefined,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 2,
          bgcolor: color.bg,
          color: color.color,
          display: "grid",
          placeItems: "center",
          flex: "0 0 auto",
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase" }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600, lineHeight: 1.35, overflowWrap: "anywhere" }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
};

const JobOpeningsChip = ({ businessId, businessUrl }) => {
  const { data } = useQuery(GET_JOB_POSTINGS_BY_BUSINESS, {
    variables: { businessId, onlyActive: true, limit: 25, offset: 0 },
    skip: !businessId || !businessUrl,
    fetchPolicy: "cache-first",
  });
  const openJobsCount = Array.isArray(data?.jobPostingsByBusiness)
    ? data.jobPostingsByBusiness.filter((job) => job?.isActive !== false).length
    : 0;

  if (!openJobsCount) return null;

  return (
    <Chip
      size="small"
      icon={<WorkOutlineIcon />}
      label={openJobsCount === 1 ? "1 job" : `${openJobsCount} jobs`}
      component={RouterLink}
      to={`/b/${businessUrl}/jobs`}
      clickable
      onClick={(event) => event.stopPropagation()}
      variant="outlined"
      sx={{ fontSize: 12, fontWeight: 800, bgcolor: "#fff7e6", borderColor: "#f3d18a" }}
    />
  );
};

const CareRecordCard = ({ business, address, title, onClick }) => {
  const category = businessCategoryLabel(business);
  const cityState = [business?.cityName, business?.stateCode].filter(Boolean).join(", ");
  const locationLabel = address || cityState || "Location not listed";
  const phone = firstPhoneNumber(business);
  const county = cleanText(business?.countyName);
  const viewCount = Number(business?.viewCount ?? business?.views ?? 0);
  const statusLabel = publicCareStatusLabel(business);
  const isActive = statusLabel === "Active";
  const addedLabel = businessAddedLabel(business);
  const hasPublicBooking =
    Boolean(business?.url) &&
    (
      business?.businessBasic?.displayOnBookingPage === true ||
      (Array.isArray(business?.businessBookingTypes) &&
        business.businessBookingTypes.some((bookingType) => bookingType?.isPublic !== false))
    );
  const publicBookingHref = business?.url ? `/b/${business.url}/booking` : "";

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
        overflow: "hidden",
        transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.10)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: "100%", alignItems: "stretch", p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "text.primary",
                  fontWeight: 800,
                  lineHeight: 1.2,
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  overflowWrap: "anywhere",
                }}
              >
                {title}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                {category}
              </Typography>
            </Box>
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                bgcolor: isActive ? "grey.900" : "grey.200",
                color: isActive ? "common.white" : "text.secondary",
                fontWeight: 700,
                flex: "0 0 auto",
              }}
            />
          </Stack>

          <Grid container spacing={{ xs: 1.75, sm: 2.25 }}>
            <Grid item xs={12} sm={6} lg={4}>
              <CareRecordFact
                icon={LocationOnOutlinedIcon}
                label="Location"
                value={locationLabel}
                tone="blue"
              />
              {county ? (
                <Typography variant="caption" sx={{ color: "text.secondary", pl: 5.5 }}>
                  {county}
                </Typography>
              ) : null}
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <CareRecordFact icon={ArticleOutlinedIcon} label="License" value={businessLicense(business)} tone="purple" />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <CareRecordFact icon={HotelOutlinedIcon} label="Beds" value={businessBeds(business)} tone="green" />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <CareRecordFact
                icon={PhoneOutlinedIcon}
                label="Contact"
                value={phone}
                tone="blue"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <CareRecordFact icon={MedicalServicesOutlinedIcon} label="Specialty" value={businessSpecialty(business)} tone="rose" />
            </Grid>
          </Grid>

          <Divider />

          {(hasPublicBooking || business?.id) ? (
            <Stack direction="row" spacing={0.75} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
                {hasPublicBooking ? (
                  <Chip
                    size="small"
                    icon={<EventIcon />}
                    label="Book Online"
                    component={RouterLink}
                    to={publicBookingHref}
                    clickable
                    onClick={(event) => event.stopPropagation()}
                    variant="outlined"
                    sx={{ fontSize: 12, fontWeight: 800 }}
                  />
                ) : null}
                <JobOpeningsChip businessId={business?.id} businessUrl={business?.url} />
            </Stack>
          ) : null}

          <Stack direction="row" spacing={2} alignItems="center" sx={{ color: "text.secondary", flexWrap: "wrap", rowGap: 0.75 }}>
            {addedLabel ? (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <CalendarTodayOutlinedIcon sx={{ fontSize: 15 }} />
                <Typography variant="caption">{addedLabel}</Typography>
              </Stack>
            ) : null}
            {viewCount > 0 ? (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">{`${viewCount} views`}</Typography>
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      </CardActionArea>
    </Paper>
  );
};

/* ===========================================================
   Page
   =========================================================== */
export default function CustomerHome() {
  const apolloClient = useApolloClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { labels, id: verticalId } = useVertical();
  const navigate = useNavigate();
  const location = useLocation();
  const initialSearchRef = useRef(location.search || "");
  const headerRef = useRef(null);
  // --- keyboard/viewport + header behavior (UI-only) ---
  const [kbInset, setKbInset] = useState(0);
  const [heroCollapsed, setHeroCollapsed] = useState(false);
  const [forceExpanded, setForceExpanded] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const lastYRef = useRef(typeof window !== "undefined" ? window.scrollY : 0);
  const searchRef = useRef(null);
  const hideLandingScrollbar = verticalId === "care";

  // query/filter state
  const [query, setQuery] = useState("");
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [expandedView, setExpandedView] = useState(false);
  const [selectedStateCode, setSelectedStateCode] = useState("");
  const [selectedCity, setSelectedCity] = useState({ id: null, name: "" });
  const [expandedStateCode, setExpandedStateCode] = useState(null);

  // left pane lists
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);

  // Advanced open/collapse
  const [advOpen, setAdvOpen] = useState(false);

  // DRAFT advanced
  const [draftZip, setDraftZip] = useState("");
  const [draftRadius, setDraftRadius] = useState(DEFAULT_RADIUS);
  const [draftFeatures, setDraftFeatures] = useState(() => createDefaultAdvancedFeatures());

  // APPLIED advanced
  const [advZip, setAdvZip] = useState("");
  const [advRadius, setAdvRadius] = useState(0);
  const [advFeatures, setAdvFeatures] = useState(() => createDefaultAdvancedFeatures());

  // paging for results
  const [accItems, setAccItems] = useState([]);
  const [nextPage, setNextPage] = useState(1); // kept for inertia; offset now uses accItems.length
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hasHydratedList, setHasHydratedList] = useState(false);
  const [globalNameFallbackActive, setGlobalNameFallbackActive] = useState(false);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(false);

  // claim dialog
  const [claimedIds, setClaimedIds] = useState(new Set());
  const [claimOpen, setClaimOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [claimTarget, setClaimTarget] = useState(null);
  const [license, setLicense] = useState("");
  const [claimError, setClaimError] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const claimSubmitInFlight = useRef(false);

  // Results query â€” tuned for instant paint on cached/partial data
  const [loadBusinesses] = useLazyQuery(BUSINESSES_PAGED, {
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-and-network",
    returnPartialData: true,
    partialRefetch: true,
    notifyOnNetworkStatusChange: true,
    context: { fetchOptions: { keepalive: true } },
  });
  const [searchBusinesses] = useLazyQuery(SEARCH_BUSINESSES, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    returnPartialData: false,
    partialRefetch: false,
    notifyOnNetworkStatusChange: true,
  });

  // State/city queries
  const [loadStates] = useLazyQuery(BUSINESS_STATES, {
    fetchPolicy: "network-only",
  });
  const [loadCities] = useLazyQuery(BUSINESS_CITIES, {
    fetchPolicy: "cache-first",
  });

  // Claim mutation
  const [claimAndMap, { loading: claimLoading }] = useMutation(CLAIM_AND_MAP, { awaitRefetchQueries: true });
  const [updateDefaultBusiness] = useMutation(UPDATE_DEFAULT_BUSINESS);

  // Debounce main text box
  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const forceListFromInitialUrl = useMemo(() => {
    const params = new URLSearchParams(initialSearchRef.current || "");
    return params.get("view") === "list";
  }, []);
  const canShowBusinessResults =
    forceListFromInitialUrl ||
    Boolean(debouncedQuery) ||
    selectedCity.id !== null ||
    Boolean(expandedStateCode);

  // If user types a ZIP in main box, clear city
  useEffect(() => {
    if (!debouncedQuery) return;
    if (isZip(debouncedQuery) && selectedCity.id !== null) {
      setSelectedCity({ id: null, name: "" });
    }
  }, [debouncedQuery, selectedCity.id]);

  // ZIP in advanced clears city
  useEffect(() => {
    if (advZip && isZip(advZip) && selectedCity.id !== null) {
      setSelectedCity({ id: null, name: "" });
    }
  }, [advZip, selectedCity.id]);

  /**
   * âœ… Build query variables using ABSOLUTE OFFSET.
   * - First page: limit=INITIAL_LIMIT, offset=0
   * - Load more:  limit=PAGE_LIMIT,   offset=accItems.length
   * This avoids skipping/duplicating when page sizes differ (4 then 8).
   */
  const varsForPage = useCallback((limit, offset) => {
    const zip = (advZip || "").trim();
    const zipIsValid = isZip(zip);
    const radiusMiles = zipIsValid && Number(advRadius || 0) > 0 ? Number(advRadius) : null;

    return {
      countryId: COUNTRY_ID,
      stateCode: selectedStateCode || null,
      cityId: selectedCity.id ?? null,
      search: debouncedQuery || null,
      limit,
      offset, // <-- absolute offset
      advZip: zip || null,
      radiusMiles,
      amenities: buildAmenitiesInput(advFeatures),
    };
  }, [selectedStateCode, selectedCity.id, debouncedQuery, advZip, advRadius, advFeatures]);

  const varsForGlobalNamePage = useCallback((limit, offset) => ({
    countryId: COUNTRY_ID,
    stateCode: null,
    cityId: null,
    search: debouncedQuery || null,
    limit,
    offset,
    advZip: null,
    radiusMiles: null,
    amenities: null,
  }), [debouncedQuery]);

  const varsForFeaturedPage = useCallback((limit = DEFAULT_BROWSE_LIMIT, offset = 0, claimed = null) => ({
    countryId: COUNTRY_ID,
    stateCode: null,
    cityId: null,
    search: null,
    limit,
    offset,
    advZip: null,
    radiusMiles: null,
    amenities: null,
    claimed,
  }), []);

  // Reset page when APPLIED filters change
  useEffect(() => {
    if (!canShowBusinessResults) {
      startTransition(() => {
        setAccItems([]);
        setNextPage(1);
        setHasMore(false);
        setGlobalNameFallbackActive(false);
        setHasHydratedList(false);
      });
      return;
    }
    startTransition(() => {
      setAccItems([]);
      setNextPage(1);
      setHasMore(true);
      setGlobalNameFallbackActive(false);
      setHasHydratedList(false);
    });
  }, [canShowBusinessResults, selectedStateCode, selectedCity.id, debouncedQuery, advZip, advRadius, advFeatures]);

  // Try session cache for results (reads)
  useEffect(() => {
    if (!canShowBusinessResults) return;
    const key = `hp:${selectedStateCode}:${selectedCity.id ?? "all"}:${advZip || ""}:${advRadius || 0}:${encodeAmenities(advFeatures)}:${(query || "").trim()}`;
    const raw = sessionStorage.getItem(key);
    if (raw) {
      try {
        const cached = JSON.parse(raw);
        if (Array.isArray(cached.items) && cached.items.length) {
          const sorted = sortByMatchThenClaimed(cached.items, claimedIds, debouncedQuery);
          setAccItems(sorted);
          setHasHydratedList(true);
          setHasMore(!!cached.hasMore);
          setNextPage(cached.nextPage || 2);
        }
      } catch { }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShowBusinessResults, selectedStateCode, selectedCity.id, advZip, advRadius, advFeatures, query, claimedIds, debouncedQuery]);

  // Initial page (small) for faster paint
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!canShowBusinessResults) return;
      if (nextPage !== 1 || accItems.length !== 0) return;
      setLoadingMore(true);
      try {
        let queryVars = varsForPage(INITIAL_LIMIT, 0);
        let { data } = await loadBusinesses({ variables: queryVars }); // offset 0
        if (ignore) return;
        let total = data?.businessesPaged?.total ?? 0;
        let items = data?.businessesPaged?.items ?? [];
        let usedSearchApiFallback = false;

        const hasNarrowingFilters =
          Boolean(selectedStateCode) ||
          selectedCity.id !== null ||
          Boolean((advZip || "").trim()) ||
          Number(advRadius || 0) > 0 ||
          Boolean(buildAmenitiesInput(advFeatures));

        if (debouncedQuery && hasNarrowingFilters && items.length === 0) {
          queryVars = varsForGlobalNamePage(INITIAL_LIMIT, 0);
          const fallbackRes = await loadBusinesses({ variables: queryVars });
          if (ignore) return;
          total = fallbackRes?.data?.businessesPaged?.total ?? 0;
          items = fallbackRes?.data?.businessesPaged?.items ?? [];
          setGlobalNameFallbackActive(items.length > 0);
        }

        if (debouncedQuery && items.length === 0) {
          const searchPayload = JSON.stringify({
            searchQuery: debouncedQuery,
            locationQuery: "",
            dateQuery: null,
            countryId: COUNTRY_ID,
          });
          const searchRes = await searchBusinesses({
            variables: { query: searchPayload },
          });
          if (ignore) return;
          const apiItems = Array.isArray(searchRes?.data?.search)
            ? searchRes.data.search
            : [];
          if (apiItems.length > 0) {
            items = apiItems;
            total = apiItems.length;
            usedSearchApiFallback = true;
            setGlobalNameFallbackActive(false);
          }
        }

        startTransition(() => {
          const first = sortByMatchThenClaimed(items, claimedIds, debouncedQuery);
          setAccItems(first);
          setHasHydratedList(true);
          setHasMore(usedSearchApiFallback ? false : first.length < total); // âœ… robust hasMore
          setNextPage(2);
        });
      } finally {
        if (!ignore) setLoadingMore(false);
      }
    })();
    return () => { ignore = true; };
  }, [canShowBusinessResults, accItems.length, loadBusinesses, nextPage, varsForPage, varsForGlobalNamePage, claimedIds, debouncedQuery, selectedStateCode, selectedCity.id, advZip, advRadius, advFeatures, searchBusinesses]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (canShowBusinessResults) return;
      if (featuredItems.length > 0) return;
      setFeaturedLoading(true);
      try {
        const { data } = await loadBusinesses({
          variables: varsForFeaturedPage(FEATURED_CLAIMED_LIMIT, 0, "claimed"),
        });
        if (ignore) return;
        let items = Array.isArray(data?.businessesPaged?.items) ? data.businessesPaged.items : [];
        if (items.length === 0) {
          const fallback = await loadBusinesses({ variables: varsForFeaturedPage() });
          if (ignore) return;
          items = Array.isArray(fallback?.data?.businessesPaged?.items)
            ? fallback.data.businessesPaged.items
            : [];
        }
        startTransition(() => {
          setFeaturedItems(items);
        });
      } finally {
        if (!ignore) setFeaturedLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [canShowBusinessResults, featuredItems.length, loadBusinesses, varsForFeaturedPage]);

  // Reflect applied filters to URL
  const navigateRef = useRef(navigate);
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (query?.trim()) params.set("q", query.trim()); else params.delete("q");
    if (selectedStateCode) params.set("st", selectedStateCode); else params.delete("st");
    if (selectedCity.id != null) params.set("cityId", String(selectedCity.id)); else params.delete("cityId");

    if (advZip?.trim()) params.set("azip", advZip.trim()); else params.delete("azip");
    if (advZip?.trim() && Number(advRadius || 0) > 0) params.set("rad", String(advRadius)); else params.delete("rad");

    const amen = encodeAmenities(advFeatures);
    if (amen) params.set("amen", amen); else params.delete("amen");
    const gender = (advFeatures.gender || "Any").trim();
    if (gender && gender !== "Any") params.set("gender", gender); else params.delete("gender");

    if (expandedView) params.set("view", "grid"); else params.delete("view");
    if (advOpen) params.set("adv", "1"); else params.delete("adv");

    const nextSearch = params.toString() ? `?${params.toString()}` : "";
    const nextUrl = `${location.pathname}${nextSearch}${location.hash || ""}`;

    if (nextUrl !== `${location.pathname}${location.search}${location.hash || ""}`) {
      navigateRef.current(nextUrl, { replace: true });
    }
  }, [
    query, selectedStateCode, selectedCity.id,
    advZip, advRadius, advFeatures,
    expandedView, advOpen,
    location.pathname, location.hash, location.search
  ]);

  // Hydrate from URL once
  const didHydrateFromUrl = useRef(false);
  useEffect(() => {
    if (didHydrateFromUrl.current) return;

    const params = new URLSearchParams(location.search);

    const q = params.get("q") || "";
    const st = params.get("st") || selectedStateCode;
    const cityIdParam = params.get("cityId");
    const azip = params.get("azip") || "";
    const rad = Number(params.get("rad") || 0) || 0;
    const amen = decodeAmenities(params.get("amen"));
    const gender = params.get("gender");
    const view = params.get("view");
    const adv = params.get("adv") === "1";

    if (q) setQuery(q);
    if (st) setSelectedStateCode(st);
    if (cityIdParam !== null) setSelectedCity({ id: Number(cityIdParam), name: "" });
    if (azip) setAdvZip(azip);
    if (rad) setAdvRadius(rad);
    if (amen || gender) {
      setAdvFeatures((features) => ({
        ...features,
        ...(amen || {}),
        ...(gender ? { gender } : {}),
      }));
    }
    if (view) setExpandedView(view === "grid");
    setAdvOpen(adv);

    didHydrateFromUrl.current = true;
  }, [location.search, selectedStateCode]);

  /* ---------- Idle-load states & cities ---------- */
  const scheduleIdle = useCallback((fn) => {
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const h = window.requestIdleCallback(fn, { timeout: 800 });
      return () => window.cancelIdleCallback?.(h);
    }
    const t = setTimeout(fn, 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const STATES_CACHE_KEY = `states:v2:${COUNTRY_ID}`;
    const cached = getWithTTL(STATES_CACHE_KEY, STATES_TTL);
    if (cached?.length) {
      setStatesList(cached);
      return;
    }
    return scheduleIdle(() => {
      loadStates({ variables: { countryId: COUNTRY_ID } }).then((res) => {
        const list = res?.data?.businessStates ?? [];
        if (list.length) {
          setStatesList(list);
          setWithTTL(STATES_CACHE_KEY, list, STATES_TTL);
        }
      }).catch(() => { });
    });
  }, [loadStates, scheduleIdle]);

  useEffect(() => {
    if (!statesList?.length || !selectedStateCode) return;
    const has = statesList.some((s) => s.stateCode === selectedStateCode);
    if (!has) {
      setSelectedStateCode("");
      setSelectedCity({ id: null, name: "" });
    }
  }, [statesList, selectedStateCode]);

  useEffect(() => {
    if (!selectedStateCode) return;
    const cacheKey = `cities:v1:${COUNTRY_ID}:${selectedStateCode}`;
    const cached = getWithTTL(cacheKey, CITIES_TTL);
    if (cached?.length) {
      setCitiesList(cached);
      return;
    }
    return scheduleIdle(() => {
      loadCities({ variables: { countryId: COUNTRY_ID, stateCode: selectedStateCode } }).then((res) => {
        const list = res?.data?.businessCities ?? [];
        setCitiesList(list);
        if (list.length) setWithTTL(cacheKey, list, CITIES_TTL);
      }).catch(() => { });
    });
  }, [selectedStateCode, loadCities, scheduleIdle]);

  /* ---------- Advanced derived ---------- */
  const activeFeatureKeys = useMemo(() => {
    return BOOLEAN_AMENITY_KEYS.filter((k) => !!advFeatures[k]);
  }, [advFeatures]);

  const advZipTrim = (advZip || "").trim();
  const advZipIsValid = isZip(advZipTrim);
  const appliedFiltersCount =
    (advZipTrim ? 1 : 0) +
    ((advZipIsValid && advRadius) ? 1 : 0) +
    activeFeatureKeys.length +
    ((advFeatures.gender || "Any") !== "Any" ? 1 : 0);
  const hasApplied = appliedFiltersCount > 0;

  // Keep draft in sync when panel opens
  useEffect(() => {
    if (!advOpen) return;
    setDraftZip(advZip || "");
    setDraftRadius(Number(advRadius || 0) || DEFAULT_RADIUS);
    setDraftFeatures({ ...advFeatures });
  }, [advOpen, advZip, advRadius, advFeatures]);

  const draftDifferent =
    (draftZip.trim() || "") !== (advZip.trim() || "") ||
    Number(draftRadius || 0) !== Number(advRadius || 0) ||
    !shallowEq(draftFeatures, advFeatures);

  const clearAdvanced = useCallback(() => {
    startTransition(() => {
      setAdvZip("");
      setAdvRadius(0);
      setAdvFeatures(createDefaultAdvancedFeatures());
    });
  }, []);

  const returnToLanding = useCallback(() => {
    setShowSearchSuggestions(false);
    setAdvOpen(false);
    setExpandedStateCode(null);
    setSelectedStateCode("");
    setSelectedCity({ id: null, name: "" });
    setQuery("");
    clearAdvanced();
  }, [clearAdvanced]);

  const handleApplyAdvanced = useCallback(() => {
    setAdvZip((draftZip || "").trim());
    setAdvRadius(Number(draftRadius || 0) || 0);
    setAdvFeatures({ ...draftFeatures });
  }, [draftZip, draftRadius, draftFeatures]);

  /* ---------- Claim flow ---------- */
  const openClaim = useCallback((b) => {
    setClaimTarget(b);
    setLicense("");
    setClaimError(null);
    setClaimOpen(true);
  }, []);

  const handleClaimSubmit = useCallback(async () => {
    if (claimSubmitInFlight.current) return;
    claimSubmitInFlight.current = true;

    if (!claimTarget) {
      claimSubmitInFlight.current = false;
      return;
    }

    const inputValue = normalizeLicense(license);
    if (!inputValue || inputValue.length < 4) {
      setClaimError("Please enter your license number.");
      claimSubmitInFlight.current = false;
      return;
    }

    const token = getToken();
    if (!token) {
      const payload = {
        businessId: claimTarget.id,
        business: {
          id: claimTarget.id, name: claimTarget.name,
          street: claimTarget.street, cityName: claimTarget.cityName,
          stateCode: claimTarget.stateCode, zipcode: claimTarget.zipcode,
          url: claimTarget.url, images: claimTarget.images ?? [],
        },
        license: inputValue,
      };
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload));
      const returnTo = encodeURIComponent("/care/claim-resume");
      setClaimError(
        <span>
          <MuiLink component={RouterLink} to={`/login?returnTo=${returnTo}`}>Log in</MuiLink>
          &nbsp;or&nbsp;
          <MuiLink component={RouterLink} to={`/signup?returnTo=${returnTo}`}>Sign up</MuiLink>
          &nbsp;to complete your claim.
        </span>
      );
      claimSubmitInFlight.current = false;
      return;
    }

    setClaimError(null);
    setClaiming(true);

    try {
      try {
        const activeToken = getToken();
        const decodedActiveToken = activeToken ? jwtDecode(activeToken) : null;
        sessionStorage.setItem(
          AGREEMENT_CONTEXT_KEY,
          JSON.stringify({
            defaultBusinessId: Number(decodedActiveToken?.defaultBusinessId || 0),
            selectedBusinessId: Number(localStorage.getItem("selectedBusinessId") || 0),
          })
        );
      } catch (_err) {
        // ignore storage failures
      }

      const { data } = await claimAndMap({
        variables: {
          input: {
            businessId: Number(claimTarget.id),
            licenseNumber: inputValue,
            treatAlreadyClaimedAsSuccess: true,
          },
        },
        refetchQueries: [{ query: BUSINESSES_PAGED, variables: varsForPage(INITIAL_LIMIT, 0) }],
      });
      const payload = data?.claimAndMapBusiness;
      if (!payload?.success) throw new Error(payload?.message || "Failed to claim & map");
      const resolvedBusinessId = Number(payload?.businessId || claimTarget.id);
      const resolvedToken = payload?.token || getToken();
      if (payload.token) setToken(payload.token);
      await updateDefaultBusiness({
        variables: { defaultBusinessId: resolvedBusinessId },
      });
      const decoded = resolvedToken ? jwtDecode(resolvedToken) : null;
      if (Number(decoded?.userId || 0) > 0) {
        const refreshedTokenResult = await apolloClient.query({
          query: GET_TOKEN,
          variables: {
            userId: Number(decoded.userId),
            businessId: resolvedBusinessId,
            staffId: null,
            subscriptionId: null,
          },
          fetchPolicy: "network-only",
        });
        const refreshedToken = refreshedTokenResult?.data?.getToken?.token;
        if (refreshedToken) {
          setToken(refreshedToken);
        }
      }
      try {
        localStorage.setItem(
          "selectedBusinessId",
          String(resolvedBusinessId)
        );
      } catch (_err) {
        // ignore storage failures
      }

      setClaimedIds((prev) => {
        const next = new Set(prev);
        next.add(idOf({ id: claimTarget.id })); // normalize ID
        setAccItems((curr) => sortByMatchThenClaimed(curr, next, debouncedQuery)); // visually re-sort
        return next;
      });
      window.location.assign(DASHBOARD_URL);
      setClaimOpen(false);
    } catch (err) {
      setClaimError(err?.message || "Unable to complete claim. Please try again.");
    } finally {
      setClaiming(false);
      claimSubmitInFlight.current = false;
    }
  }, [apolloClient, claimTarget, license, claimAndMap, debouncedQuery, updateDefaultBusiness, varsForPage]);

  /* ---------- Focus/viewport management ---------- */
  const ensureSearchVisible = useCallback(() => {
    const el = searchRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const vv = window.visualViewport;
    const vvTop = vv?.offsetTop ?? 0;
    const vvHeight = vv?.height ?? window.innerHeight;

    const margin = 12;
    const desiredTop = vvTop + margin;
    const desiredBottom = vvTop + vvHeight - margin;

    if (rect.top < desiredTop) {
      const delta = rect.top - desiredTop;
      window.scrollBy({ top: delta, behavior: "smooth" });
    } else if (rect.bottom > desiredBottom) {
      const delta = rect.bottom - desiredBottom;
      window.scrollBy({ top: delta, behavior: "smooth" });
    }
  }, []);

  const onSearchFocus = useCallback(() => {
    setShowSearchSuggestions(true);
    if (!isMobile) return;

    setIsSearchActive(true);
    setHeroCollapsed(true);

    ensureSearchVisible();
    requestAnimationFrame(ensureSearchVisible);
    setTimeout(ensureSearchVisible, 220);
    setTimeout(ensureSearchVisible, 420);
  }, [isMobile, ensureSearchVisible]);

  const onSearchBlur = useCallback(() => {
    // Delay close so click on a suggestion can register.
    setTimeout(() => setShowSearchSuggestions(false), 120);
    if (!isMobile) return;
    setIsSearchActive(false);
    if (!query.trim()) setHeroCollapsed(false);
  }, [isMobile, query]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - (lastYRef.current ?? 0);
      lastYRef.current = y;

      if (isSearchActive && dy < -8) {
        const el = searchRef.current;
        if (el && document.activeElement === el) el.blur();
        setIsSearchActive(false);
        if (!query.trim()) setHeroCollapsed(false);
        return;
      }

      if (document.activeElement === searchRef.current || isSearchActive) return;

      if (dy < -12) {
        if (!forceExpanded) {
          setForceExpanded(true);
          setHeroCollapsed(false);
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isSearchActive, forceExpanded, query]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const overlap = window.innerHeight - (vv.height + vv.offsetTop);
      setKbInset(Math.max(0, Math.round(overlap)));
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  useEffect(() => {
    if (!hideLandingScrollbar || typeof document === "undefined") return undefined;

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlScrollbarWidth = html.style.scrollbarWidth;
    const previousBodyScrollbarWidth = body.style.scrollbarWidth;
    const previousMsOverflowStyle = body.style.msOverflowStyle;

    html.style.scrollbarWidth = "none";
    body.style.scrollbarWidth = "none";
    body.style.msOverflowStyle = "none";

    const styleTag = document.createElement("style");
    styleTag.setAttribute("data-care-landing-scrollbar", "true");
    styleTag.textContent = `
      html::-webkit-scrollbar,
      body::-webkit-scrollbar {
        width: 0 !important;
        height: 0 !important;
        display: none !important;
        background: transparent !important;
      }
    `;
    document.head.appendChild(styleTag);

    return () => {
      html.style.scrollbarWidth = previousHtmlScrollbarWidth;
      body.style.scrollbarWidth = previousBodyScrollbarWidth;
      body.style.msOverflowStyle = previousMsOverflowStyle;
      styleTag.remove();
    };
  }, [hideLandingScrollbar]);

  const visibleAccItems = useMemo(
    () => {
      const careItems = verticalId === "care" ? accItems.filter(isCareDirectoryBusiness) : accItems;
      return careItems;
    },
    [accItems, verticalId]
  );
  const visibleFeaturedItems = useMemo(
    () => {
      const careItems = verticalId === "care" ? featuredItems.filter(isCareDirectoryBusiness) : featuredItems;
      return careItems;
    },
    [featuredItems, verticalId]
  );

  /* ---------- Render ---------- */
  const isInitialLoading = !hasHydratedList && loadingMore;
  const isTrulyEmpty = hasHydratedList && visibleAccItems.length === 0 && !loadingMore;
  const showResultsToolbar = canShowBusinessResults && !isTrulyEmpty;
  const isSticky = isMobile && heroCollapsed && !forceExpanded;
  const normalizedQuery = query.trim().toLowerCase();
  const isTypingActive = isMobile && isSearchActive;
  const searchHeading = labels?.searchHeader1 || "Browse care homes";
  const resultsHeading = selectedCity.name
    ? [selectedStateCode, selectedCity.name].filter(Boolean).join(" - ")
    : selectedStateCode || (debouncedQuery ? `Results for "${debouncedQuery}"` : "");
  const searchSuggestions = useMemo(() => {
    return buildHomeSearchSuggestions({
      accItems,
      citiesList,
      normalizedQuery,
      selectedStateCode,
      statesList,
    });
  }, [normalizedQuery, accItems, citiesList, statesList, selectedStateCode]);

  const handlePickSuggestion = useCallback((s) => {
    setShowSearchSuggestions(false);
    if (!s) return;

    if (s.type === "business") {
      setQuery(s.value || "");
      if (s.stateCode) setSelectedStateCode(s.stateCode);
      return;
    }

    if (s.type === "city") {
      setQuery(s.value || "");
      setSelectedCity({ id: s.cityId ?? null, name: s.value || "" });
      return;
    }

    if (s.type === "state") {
      if (s.stateCode) {
        setSelectedStateCode(s.stateCode);
        setSelectedCity({ id: null, name: "" });
        setExpandedStateCode(s.stateCode);
        setQuery(s.stateCode);
      }
    }
  }, []);

  const CardFallback = ({ lines = 2 }) => (
    <Box>
      <Skeleton variant="rounded" height={220} />
      {[...Array(lines)].map((_, i) => (
        <Skeleton key={i} variant="text" sx={{ mt: i === 0 ? 1 : 0, width: i === 0 ? "80%" : "60%" }} />
      ))}
    </Box>
  );

  const renderBusinessGrid = useCallback((items, skeletonCount = 0) => (
    <Grid container spacing={{ xs: 1, sm: 2, md: 2.5 }}>
      {items.map((b, idx) => {
        const address = [b.street, b.cityName, b.stateCode, b.zipcode].filter(Boolean).join(", ");
        const formattedAddress = formatCityStateZip(address);
        const claimed = isClaimed(b, claimedIds);
        const unclaimed = !claimed;
        const openCount = Number(b?.careMeta?.bedsOpen ?? 0);
        const imageUrl = b?.images?.[0]?.url ?? officePlaceHolderImage;
        const cardTitle =
          debouncedQuery && debouncedQuery.length > 0
            ? `${b?.stateCode || "--"} | ${b?.name || ""}`
            : b.name;
        const isCareResult = verticalId === "care";
        const gridCols = expandedView
          ? { xs: 12 }
          : isCareResult
            ? { xs: 12, md: 6 }
            : { xs: 12, sm: 6, md: 3, lg: 3, xl: 3 };
        const handleOpenDetails = () => {
          const { id: businessId, name, url, images } = b;
          const to = (HOME_ADAPTERS[verticalId] ?? HOME_ADAPTERS.care).getCardUrl?.(b) ?? ("/" + (url ?? ""));
          navigate(to, { state: { business: { id: businessId, name, address, url, images: images ?? [] } } });
        };

        return (
          <Grid key={idOf(b)} item {...gridCols} sx={{ minWidth: 0, contentVisibility: "auto", containIntrinsicSize: "300px 320px" }}>
            <Suspense fallback={<CardFallback />}>
              {isCareResult ? (
                <CareRecordCard
                  business={b}
                  address={formattedAddress}
                  title={cardTitle}
                  onClick={handleOpenDetails}
                />
              ) : (
                <ResponsiveBusinessCard
                  title={cardTitle}
                  address={formattedAddress}
                  image={imageUrl}
                  ratio={expandedView ? "12/5" : undefined}
                  minImageHeight={expandedView ? { xs: 145, sm: 175, md: 210 } : undefined}
                  imageFit={expandedView ? "contain" : undefined}
                  mediaSx={expandedView ? { bgcolor: "#eef3f7" } : undefined}
                  cardSx={expandedView ? { maxWidth: 860, mx: "auto" } : undefined}
                  overlayLeftLabel={unclaimed ? "Unclaimed" : undefined}
                  overlayRight={
                    claimed && openCount > 0 ? (
                      <Box sx={{ position: "relative", zIndex: 3, pointerEvents: "none" }}>
                        <Suspense fallback={null}>
                          <AvailableBedsBadge businessId={b.id} dense openCount={openCount} showCount={false} />
                        </Suspense>
                      </Box>
                    ) : null
                  }
                  onClick={handleOpenDetails}
                  imageHints={{
                    highPriority: idx < (isMobile ? 2 : 4),
                    sizes: expandedView
                      ? "(min-width: 600px) 800px, 92vw"
                      : "(min-width: 1200px) 25vw, (min-width: 600px) 45vw, 92vw",
                  }}
                />
              )}
              {!isCareResult && unclaimed ? (
                <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      openClaim(b);
                    }}
                    sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      fontWeight: 700,
                    }}
                  >
                    Claim this business
                  </Button>
                </Box>
              ) : null}
            </Suspense>
          </Grid>
        );
      })}

      {skeletonCount > 0 &&
        Array.from({ length: skeletonCount }).map((_, i) => (
          <Grid
            key={`sk-${i}`}
            item
            {...(expandedView ? { xs: 12 } : { xs: 12, sm: 6, md: 3, lg: 3, xl: 3 })}
          >
            <CardFallback />
          </Grid>
        ))}
    </Grid>
  ), [claimedIds, debouncedQuery, expandedView, isMobile, navigate, openClaim, verticalId]);

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: theme.palette.background.default }}>
      <Box
        ref={headerRef}
        sx={{
          bgcolor: "#22374a",
          color: theme.palette.primary.contrastText,
          textAlign: "center",
          position: { xs: isSticky ? "sticky" : "static", sm: "static" },
          top: 0,
          zIndex: 1100,
          boxShadow: { xs: heroCollapsed ? 2 : 10, sm: 12 },
          display: "flex",
          flexDirection: "column",
          minHeight: { xs: isTypingActive ? 120 : (heroCollapsed ? 220 : 320), sm: 340 },
          pt: { xs: "calc(env(safe-area-inset-top, 0px) + 7px)", sm: 0.875 },
          pb: { xs: isTypingActive ? 1 : 2, sm: 5 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            minHeight: { xs: 112, sm: 120 },
            px: 2,
            mt: { xs: heroCollapsed ? 0 : 3, sm: 12 },
          }}
        >
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              mb: { xs: 1.25, sm: 2.5 },
              display: { xs: isTypingActive ? "none" : "block", sm: "block" },
            }}
          >
            {searchHeading}
          </Typography>

          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: 500,
              mx: "auto",
              mb: { xs: 0.75, sm: 0 },
            }}
          >
            <Box
              ref={searchRef}
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: "background.paper",
                borderRadius: 2,
                px: 1,
                py: 0.5,
                boxShadow: 1,
                height: 44,
              }}
            >
              <SearchIcon color="action" />
              <InputBase
                placeholder="Search care setting, city, zip, or provider"
                fullWidth
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShowSearchSuggestions(true);
                }}
                onFocus={onSearchFocus}
                onBlur={onSearchBlur}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (searchSuggestions.length > 0) handlePickSuggestion(searchSuggestions[0]);
                  }
                }}
                sx={{ ml: 1, fontSize: 14 }}
                inputProps={{ "aria-label": "search providers" }}
              />
              {!!query && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setQuery("");
                    setShowSearchSuggestions(false);
                  }}
                  sx={{ ml: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            {showSearchSuggestions && normalizedQuery && searchSuggestions.length > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  right: 0,
                  zIndex: 30,
                  borderRadius: 2,
                  overflow: "hidden",
                  maxHeight: 260,
                  overflowY: "auto",
                  borderColor: "rgba(214,226,240,0.95)",
                  boxShadow: "0 14px 30px rgba(15,23,42,0.16)",
                }}
              >
                <List dense disablePadding>
                  {searchSuggestions.map((s) => (
                    <ListItemButton
                      key={s.key}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handlePickSuggestion(s);
                      }}
                      sx={{ py: 0.9, px: 1.2, borderBottom: "1px solid #eef3f9" }}
                    >
                      <ListItemText
                        primary={s.label}
                        primaryTypographyProps={{ fontSize: 13, fontWeight: 600, color: "#24384f" }}
                        secondary={s.type === "business" ? "Business" : s.type === "city" ? "City" : "State"}
                        secondaryTypographyProps={{ fontSize: 11, color: "#6d7f95" }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}

            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setAdvOpen((prev) => {
                  const next = !prev;
                  if (next && (!draftRadius || Number(draftRadius) === 0)) {
                    setDraftRadius(DEFAULT_RADIUS);
                  }
                  return next;
                });
              }}
              endIcon={<ExpandMoreIcon />}
              sx={{
                color: "inherit",
                borderColor: "rgba(255,255,255,0.6)",
                "&:hover": { borderColor: "rgba(255,255,255,0.85)" },
                bgcolor: "rgba(255,255,255,0.08)",
                whiteSpace: "nowrap",
                position: { xs: "static", md: "absolute" },
                top: { md: "50%" },
                transform: { md: "translateY(-50%)" },
                right: { md: -140 },
                mt: { xs: 1, md: 0 },
                display: "inline-flex",
              }}
            >
              <Badge
                color="warning"
                badgeContent={hasApplied ? appliedFiltersCount : 0}
                invisible={!hasApplied}
                sx={{ "& .MuiBadge-badge": { right: -8, top: -8 } }}
              >
                More filters
              </Badge>
            </Button>
          </Box>

          <Box
            sx={{
              mt: 1,
              width: "100%",
              maxWidth: 500,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              component={RouterLink}
              to="/business/register"
              size="small"
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: 999,
                px: 2,
                py: 0.7,
                fontWeight: 700,
                bgcolor: "rgba(255,255,255,0.18)",
                color: "#ffffff",
                border: "1px solid rgba(255,255,255,0.35)",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.26)",
                  boxShadow: "none",
                },
              }}
            >
              <Box component="span">Update your care listing</Box>
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 0.9,
                  py: 0.18,
                  borderRadius: 999,
                  bgcolor: "#facc15",
                  color: "#2f2500",
                  fontSize: "0.72rem",
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                Free listing
              </Box>
            </Button>
          </Box>

          <Collapse in={advOpen} timeout="auto" unmountOnExit={false}>
            <Box sx={{ mt: 1, width: "100%", maxWidth: 768 }}>
              <AdvancedSearchInline
                draftZip={draftZip}
                setDraftZip={setDraftZip}
                draftRadius={draftRadius}
                setDraftRadius={setDraftRadius}
                draftFeatures={draftFeatures}
                setDraftFeatures={setDraftFeatures}
                onApply={handleApplyAdvanced}
                applyDisabled={!draftDifferent}
                onClose={() => setAdvOpen(false)}
              />
            </Box>
          </Collapse>

          {hasApplied && (
            <Box sx={{ mt: 1, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 1,
                  py: 0.7,
                  borderRadius: 2.2,
                  maxWidth: 900,
                  width: "100%",
                  mx: "auto",
                  bgcolor: "rgba(255,255,255,0.94)",
                  borderColor: "rgba(216,226,240,0.95)",
                  boxShadow: "0 8px 22px rgba(14,28,44,0.14)",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    "& .MuiChip-root": {
                      bgcolor: "#fff",
                      border: "1px solid #cfdaea",
                      color: "#2f4c6b",
                    },
                    "& .MuiChip-deleteIcon": { color: "#5c7491" },
                  }}
                >
                  {advZipTrim && (
                    <Chip size="small" label={`zip: ${advZipTrim}`} onDelete={() => { setAdvZip(""); setAdvRadius(0); }} />
                  )}
                  {(advZipIsValid && advRadius > 0) && (
                    <Chip size="small" label={`Radius: ${formatMiles(advRadius)}`} onDelete={() => setAdvRadius(0)} />
                  )}
                  {activeFeatureKeys.map((k) => (
                    <Chip
                      key={k}
                      size="small"
                      label={{
                        isPrivateRoom: "Private room",
                        hasPrivateBathroom: "Private bath",
                        hasHospitalBed: "Hospital bed",
                        hasHoyerLift: "Hoyer lift",
                        hasOxygen: "Oxygen",
                        accommodatesBariatric: "Bariatric OK",
                      }[k] || k}
                      onDelete={() => setAdvFeatures((f) => ({ ...f, [k]: false }))}
                    />
                  ))}
                  {(advFeatures.gender || "Any") !== "Any" && (
                    <Chip
                      size="small"
                      label={`Gender: ${advFeatures.gender}`}
                      onDelete={() => setAdvFeatures((f) => ({ ...f, gender: "Any" }))}
                    />
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={clearAdvanced}
                    sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      px: 1.4,
                      fontWeight: 700,
                      bgcolor: "#22374a",
                      "&:hover": { bgcolor: "#1b2f41" },
                    }}
                  >
                    Clear all
                  </Button>
                </Stack>
              </Paper>
            </Box>
          )}
        </Box>
      </Box>

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          mt: 2,
          pb: `calc(4rem + ${kbInset}px)`,
          maxWidth: "min(92vw, 1600px)",
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4, lg: 5 },
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.25 }}>
              {canShowBusinessResults ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={returnToLanding}
                  sx={{
                    textTransform: "none",
                    borderRadius: 999,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  Back to browse
                </Button>
              ) : (
                <Box />
              )}

              <Typography variant="h6" fontWeight="bold" noWrap sx={{ flex: 1 }}>
                {showResultsToolbar ? resultsHeading : ""}
              </Typography>

              {showResultsToolbar ? (
                <Tooltip title={expandedView ? "Compact view" : "Expand view"} arrow>
                  <IconButton onClick={() => setExpandedView((v) => !v)} size="small" sx={{ bgcolor: "background.paper", boxShadow: 1 }}>
                    {expandedView ? <ViewModuleIcon /> : <ViewAgendaIcon />}
                  </IconButton>
                </Tooltip>
              ) : (
                <Box />
              )}
            </Box>

            {canShowBusinessResults ? (
              isTrulyEmpty ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    border: "1px solid rgba(53,90,125,0.12)",
                    bgcolor: "rgba(255,255,255,0.92)",
                  }}
                >
                  <Typography sx={{ fontWeight: 800, color: "#14324a", mb: 0.75 }}>
                    No homes matched this search yet.
                  </Typography>
                  <Typography sx={{ color: "#4f697d", lineHeight: 1.7 }}>
                    Try another city, zip, or provider name, or clear a few filters and search again.
                  </Typography>
                </Paper>
              ) : (
                <React.Fragment>
                  {renderBusinessGrid(visibleAccItems, isInitialLoading ? 8 : 0)}

                  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={async () => {
                        if (loadingMore || !hasMore) return;
                        setLoadingMore(true);
                        try {
                          const pageVars = globalNameFallbackActive
                            ? varsForGlobalNamePage(PAGE_LIMIT, accItems.length)
                            : varsForPage(PAGE_LIMIT, accItems.length);
                          const { data } = await loadBusinesses({ variables: pageVars });
                          const total = data?.businessesPaged?.total ?? 0;
                          const items = data?.businessesPaged?.items ?? [];
                          startTransition(() => {
                            setAccItems((prev) => {
                              const next = mergeDedupeAndSort(prev, items, claimedIds, debouncedQuery);
                              setHasMore(next.length < total);
                              return next;
                            });
                            setNextPage((p) => p + 1);
                          });
                        } finally {
                          setLoadingMore(false);
                        }
                      }}
                      disabled={!hasMore || loadingMore}
                    >
                      {hasMore ? (loadingMore ? "Loading..." : "Load more") : "No more results"}
                    </Button>
                  </Box>
                </React.Fragment>
              )
            ) : (
              <Stack spacing={2.5}>
                <Box>
                  <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, color: "#14324a", mb: 0.5 }}>
                    Featured Adult Family Homes
                  </Typography>
                  <Typography sx={{ color: "#5b789a", lineHeight: 1.6, mb: 2 }}>
                    Search by city or ZIP code to find nearby Adult Family Homes, or browse featured homes below.
                  </Typography>
                  {visibleFeaturedItems.length || featuredLoading ? (
                    renderBusinessGrid(visibleFeaturedItems, featuredLoading ? 4 : 0)
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, md: 2.5 },
                        borderRadius: 3,
                        border: "1px solid rgba(53,90,125,0.12)",
                        bgcolor: "rgba(255,255,255,0.92)",
                      }}
                    >
                      <Typography sx={{ color: "#4f697d", lineHeight: 1.7 }}>
                        Featured homes are coming soon. Try searching by city or ZIP code to find care options near you.
                      </Typography>
                    </Paper>
                  )}
                </Box>
              </Stack>
            )}
          </Grid>
        </Grid>
      </Container>

      <HomeDialogs
        claimError={claimError}
        claimLoading={claimLoading}
        claimOpen={claimOpen}
        claimSubmitInFlight={claimSubmitInFlight}
        claimTarget={claimTarget}
        claiming={claiming}
        demoOpen={demoOpen}
        handleClaimSubmit={handleClaimSubmit}
        license={license}
        setClaimOpen={setClaimOpen}
        setDemoOpen={setDemoOpen}
        setLicense={setLicense}
      />
    </Box>
  );
}





